/**
 * ReportShell — shared chrome for the 10 pre-built reports under `/reports/*`.
 *
 * Each pre-built report renders this shell with:
 *   - title + description from the slug → PreBuiltReport mapping
 *   - date-range picker
 *   - "Run now" button that calls the purpose-built endpoint mapped from the
 *     reportKey (`runPreBuiltReport` in `lib/api/reports.ts`)
 *   - CSV / JSON export buttons that download the response payload
 *     client-side via `lib/csv.ts` (the backend does not expose
 *     `/v1/reporting/exports/*` for the pre-built reports)
 *   - PDF export deferred — shows a small notice rather than pretending
 *   - a child slot rendering the report's bespoke layout
 */
import { type JSX, type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Download, FileBarChart, FileText, Play } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type PreBuiltReport,
  type PreBuiltRunResult,
  runPreBuiltReport,
} from '../../lib/api/reports';
import { downloadBlob, jsonToCsv } from '../../lib/csv';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

interface ReportShellProps {
  readonly report: PreBuiltReport;
  readonly children: ReactNode;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function safeFileSegment(s: string): string {
  return s.replace(/[^a-z0-9.-]+/gi, '-').toLowerCase();
}

export function ReportShell({ report, children }: ReportShellProps): JSX.Element {
  const [fromDate, setFromDate] = useState<string>(thirtyDaysAgoIso());
  const [toDate, setToDate] = useState<string>(todayIso());
  const [pdfNotice, setPdfNotice] = useState<boolean>(false);

  const run = useMutation<PreBuiltRunResult, Error>({
    mutationFn: () => runPreBuiltReport(report.reportKey, { fromDate, toDate }),
  });

  const download = (format: 'CSV' | 'JSON'): void => {
    if (!run.data) return;
    const base = `${safeFileSegment(report.slug)}-${run.data.fromDate}_${run.data.toDate}`;
    if (format === 'JSON') {
      downloadBlob(
        `${base}.json`,
        JSON.stringify(run.data.data, null, 2),
        'application/json;charset=utf-8;',
      );
      return;
    }
    const csv = jsonToCsv(run.data.data);
    downloadBlob(
      `${base}.csv`,
      csv.length > 0 ? csv : 'no_data\n',
      'text/csv;charset=utf-8;',
    );
  };

  const denied =
    run.isError && run.error instanceof ApiError &&
    (run.error.status === 401 || run.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title={report.title}
        description={report.description}
        actions={
          <Link
            to="/reports"
            className="px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
          >
            ← All reports
          </Link>
        }
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              aria-label="From date"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              aria-label="To date"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {run.isPending ? 'Running…' : 'Run now'}
          </button>
          <div className="flex flex-wrap gap-2">
            {(['CSV', 'JSON'] as const).map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => download(fmt)}
                disabled={!run.data}
                title={run.data ? `Download as ${fmt}` : 'Run a report first'}
                data-testid={`shell-export-${fmt.toLowerCase()}`}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-3.5 h-3.5" /> {fmt}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPdfNotice(true)}
              data-testid="shell-export-pdf"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-muted border border-border rounded-lg hover:bg-gray-50"
              title="PDF export coming soon"
            >
              <FileText className="w-3.5 h-3.5" /> PDF
            </button>
          </div>
        </div>
      </div>

      {pdfNotice ? (
        <DeferredNotice title="PDF export coming soon">
          <p>
            PDF rendering for pre-built reports is on the roadmap. For now, use{' '}
            <strong>CSV</strong> or <strong>JSON</strong> — the response payload is
            captured in full.
          </p>
        </DeferredNotice>
      ) : null}

      {run.data ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileBarChart className="w-4 h-4 text-nipost-green" /> Latest run
            </h3>
            <StatusBadge
              status={
                typeof run.data.data['stub'] === 'boolean' && run.data.data['stub']
                  ? 'PENDING'
                  : 'COMPLETED'
              }
            />
          </div>
          <p className="text-xs text-muted">
            reportKey=<code className="font-mono">{run.data.reportKey}</code> ·{' '}
            range=<code className="font-mono">{run.data.fromDate} → {run.data.toDate}</code> ·{' '}
            generated=<code className="font-mono">{run.data.generatedAt}</code>
          </p>
        </div>
      ) : null}

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/read*</code> required for this report.
        </DeferredNotice>
      ) : run.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {run.error.message}
        </DeferredNotice>
      ) : null}

      {children}
    </div>
  );
}
